import React, { useEffect, useRef, useState } from 'react';
import WizardFrame from './WizardFrame';
import ConnectionAvatar from './ConnectionAvatar';
import { INTEGRATIONS_LOGO_PATH } from '../common/constants';
import {
  getMetaAccounts,
  refreshMetaAccounts,
  saveMetaMapping,
  disconnectMeta,
  deleteMetaData,
  setExclusion,
  startOAuth
} from '../../utils/api_integrations';

// ---------------------------------------------------------------- #
// MetaWizard
// ---------------------------------------------------------------- #
//
// The Facebook and Instagram wizard (plan 08-meta.md):
//   1. Connect: the connected Facebook users, "Connect Facebook &
//      Instagram", Disconnect per connection, and Delete Meta data per
//      park (the admin types DELETE to confirm).
//   2. Accounts: lists the pages and Instagram accounts again from
//      Facebook and shows the counts, with Refresh per connection.
//   3. Map parks: per park, one Facebook page and one Instagram account,
//      each with a "not needed" tick.
//   4. Review: the changes, and Save (the only step that writes mappings).
//
// Props (the same contract as every provider wizard):
//   register       The `data` of GET register
//   parkiId        The park the wizard was opened from, or null
//   initialStep    The step to open at (2 after returning from Facebook)
//   connectionKey  Unused by Meta (the parks say which connection they use)
//   canConnect     False while staff are viewing as the customer
//   onClose        Called with true when something was saved, false otherwise

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** The four step labels. */
const META_STEPS = ['Connect', 'Accounts', 'Map parks', 'Review'];

/** Step numbers, named so the code reads plainly. */
const STEP_CONNECT = 1;
const STEP_ACCOUNTS = 2;
const STEP_MAP = 3;
const STEP_REVIEW = 4;

/** Message shown on the Connect button while staff are viewing as the customer. */
const IMPERSONATING_MESSAGE = 'Ask the customer to connect their own account';

/** Text of the empty choice in each select. */
const NO_CHOICE_LABEL = 'None';

/** The word the admin must type before Meta data is deleted (the API checks it too). */
const DELETE_CONFIRM_WORD = 'DELETE';

/** Size of the scrolling park list in the delete panel. */
const DELETE_PARK_LIST_STYLE = { maxHeight: '12rem', overflowY: 'auto' };

/** Size of an Instagram profile picture next to its select. */
const PROFILE_PICTURE_STYLE = { width: '28px', height: '28px', objectFit: 'cover' };

// ---------------------------------------------------------------- #
// Helpers
// ---------------------------------------------------------------- #

/**
 * Show a database date-time ("YYYY-MM-DD HH:MM:SS") as a date only, day first ("03/11/2026"), or "" for null.
 */
const formatDate = (value) => {
  if (!value) {
    return '';
  }

  // Take the year, month and day from the start of the value
  const datePart = String(value).substring(0, 10);
  const pieces = datePart.split('-');
  const hasThreePieces = pieces.length === 3;
  if (!hasThreePieces) {
    return datePart;
  }

  // Put them back together day first
  const year = pieces[0];
  const month = pieces[1];
  const day = pieces[2];
  return `${day}/${month}/${year}`;
};

/**
 * Join names as "A", "A and B", or "A, B and C".
 */
const joinNames = (names) => {
  if (names.length <= 1) {
    return names.join('');
  }
  const allButLast = names.slice(0, names.length - 1);
  const lastName = names[names.length - 1];
  return `${allButLast.join(', ')} and ${lastName}`;
};

/**
 * Read each park's current Facebook and Instagram choices from the register cells.
 */
const buildChoicesFromRegister = (register) => {
  // One entry per park, keyed by park id as a string
  const choices = {};
  for (const park of register?.parks || []) {
    const facebookCell = park.cells?.facebook || {};
    const instagramCell = park.cells?.instagram || {};

    choices[String(park.parkiId)] = {
      fbPageId: String(facebookCell.mapping?.externalId || ''),
      igAccountId: String(instagramCell.mapping?.externalId || ''),
      facebookNotNeeded: facebookCell.notNeeded === true,
      instagramNotNeeded: instagramCell.notNeeded === true
    };
  }
  return choices;
};

/**
 * Find a park's name by id, or the id itself when the park is not in the register.
 */
const findParkName = (register, parkiId) => {
  for (const park of register?.parks || []) {
    if (String(park.parkiId) === String(parkiId)) {
      return park.parkName;
    }
  }
  return `Park ${parkiId}`;
};

/**
 * Find an account (page or Instagram) by id in a list, or null.
 */
const findAccount = (items, externalId) => {
  if (!externalId) {
    return null;
  }
  for (const item of items) {
    if (String(item.externalId) === String(externalId)) {
      return item;
    }
  }
  return null;
};

/**
 * The text an Instagram account is shown as.
 */
const instagramLabel = (account) => {
  if (account.username) {
    return `@${account.username}`;
  }
  return account.externalId;
};

/**
 * Describe a page or Instagram choice for the review list.
 */
const describeChoice = (items, externalId, labelFor) => {
  // An empty choice reads as None
  if (!externalId) {
    return NO_CHOICE_LABEL;
  }

  // Look the id up in the cached accounts
  const account = findAccount(items, externalId);
  if (account) {
    return labelFor(account);
  }

  // The id is mapped but no longer listed by Facebook
  return `${externalId} (no longer available)`;
};

/**
 * The name a Meta connection is shown as: the Facebook user's name, or "Facebook user" when Facebook gave none.
 */
const connectionDisplayName = (connection) => {
  const userName = (connection.externalUserName || '').trim();
  if (userName === '') {
    return 'Facebook user';
  }
  return userName;
};

/**
 * Describe what a connection can see, for example "11 pages and 1 Instagram account".
 */
const describeConnectionCounts = (connection) => {
  // The page count with the right word
  const pageCount = connection.pageCount || 0;
  let pageWord = 'pages';
  if (pageCount === 1) {
    pageWord = 'page';
  }

  // The Instagram count with the right word
  const instagramCount = connection.instagramCount || 0;
  let instagramWord = 'Instagram accounts';
  if (instagramCount === 1) {
    instagramWord = 'Instagram account';
  }

  return `${pageCount} ${pageWord} and ${instagramCount} ${instagramWord}`;
};

/**
 * The text a Facebook page is shown as.
 */
const pageLabel = (page) => {
  return page.name || page.externalId;
};

// ---------------------------------------------------------------- #
// The wizard
// ---------------------------------------------------------------- #

/**
 * The four-step Facebook and Instagram wizard.
 */
const MetaWizard = ({ register, parkiId = null, initialStep = 1, canConnect = true, onClose }) => {

  // ---------------------------------------------------------------- #
  // State
  // ---------------------------------------------------------------- #

  const [step, setStep] = useState(initialStep);
  const [maxReachedStep, setMaxReachedStep] = useState(initialStep);
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingId, setRefreshingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [changed, setChanged] = useState(false);
  const [savedChoices, setSavedChoices] = useState(() => buildChoicesFromRegister(register));
  const [choices, setChoices] = useState(() => buildChoicesFromRegister(register));
  const [problems, setProblems] = useState([]);
  const [saveSummary, setSaveSummary] = useState(null);
  const [parkFilter, setParkFilter] = useState('');
  const [pageFilter, setPageFilter] = useState('');
  const [instagramFilter, setInstagramFilter] = useState('');
  const [activeParkKey, setActiveParkKey] = useState(null);
  const [deletePanelOpen, setDeletePanelOpen] = useState(false);
  // No park starts ticked for deletion; the admin picks each one
  const [deleteParkKeys, setDeleteParkKeys] = useState({});
  const [deleteTyped, setDeleteTyped] = useState('');

  // Coming back from Facebook already listed the accounts, so step 2 does not list them again
  const autoRefreshDone = useRef(initialStep >= STEP_ACCOUNTS);

  // The opened park's row, so step 3 can scroll to it
  const openedParkRow = useRef(null);

  // Read the cached lists, defaulting to empty
  const connections = accounts?.connections || [];
  const facebookPages = accounts?.facebookPages || [];
  const instagramAccounts = accounts?.instagramAccounts || [];
  const parks = register?.parks || [];
  const refreshing = refreshingId !== null;

  // ---------------------------------------------------------------- #
  // Load the connections and cached accounts
  // ---------------------------------------------------------------- #

  // Read the Meta connections when the wizard opens
  useEffect(() => {
    let cancelled = false;
    const loadAccounts = async () => {
      try {
        const result = await getMetaAccounts();
        if (!cancelled) {
          setAccounts(result.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'The Facebook connections could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadAccounts();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------------------------------------------- #
  // Step navigation
  // ---------------------------------------------------------------- #

  /**
   * Show a step and remember it as reached.
   */
  const goToStep = (stepNumber) => {
    setError(null);
    setNotice(null);
    setStep(stepNumber);
    if (stepNumber > maxReachedStep) {
      setMaxReachedStep(stepNumber);
    }
  };

  // Bring the opened park's row into view when the map step opens
  useEffect(() => {
    const onMapStep = step === STEP_MAP;
    if (onMapStep && openedParkRow.current) {
      openedParkRow.current.scrollIntoView({ block: 'center' });
    }
  }, [step]);

  // ---------------------------------------------------------------- #
  // Changes
  // ---------------------------------------------------------------- #

  /**
   * List every change between the saved and the current choices, park by park.
   */
  const buildChanges = () => {
    const changes = [];
    for (const park of parks) {
      const parkKey = String(park.parkiId);
      const before = savedChoices[parkKey];
      const after = choices[parkKey];
      if (!before || !after) {
        continue;
      }

      // Compare each field
      const pageChanged = before.fbPageId !== after.fbPageId;
      const instagramChanged = before.igAccountId !== after.igAccountId;
      const facebookTickChanged = before.facebookNotNeeded !== after.facebookNotNeeded;
      const instagramTickChanged = before.instagramNotNeeded !== after.instagramNotNeeded;
      const anyChange = pageChanged || instagramChanged || facebookTickChanged || instagramTickChanged;
      if (!anyChange) {
        continue;
      }

      changes.push({
        park: park,
        before: before,
        after: after,
        mappingChanged: pageChanged || instagramChanged,
        pageChanged: pageChanged,
        instagramChanged: instagramChanged,
        facebookTickChanged: facebookTickChanged,
        instagramTickChanged: instagramTickChanged
      });
    }
    return changes;
  };

  /**
   * True when there are choices that have not been saved.
   */
  function isDirty() {
    return buildChanges().length > 0;
  }

  /**
   * Find a connection by id, or null.
   */
  const findConnection = (connectionId) => {
    for (const connection of connections) {
      if (String(connection.connectionId) === String(connectionId)) {
        return connection;
      }
    }
    return null;
  };

  /**
   * Check the changed mappings before saving: every picked account must come from a working sign-in.
   * The save sends both ids of a changed park, and the API checks both, so an unchanged id is checked too.
   */
  const checkChoices = () => {
    const found = [];
    for (const change of buildChanges()) {
      if (!change.mappingChanged) {
        continue;
      }
      const parkName = change.park.parkName;

      // A picked page must be listed and its sign-in must be working
      if (change.after.fbPageId) {
        const page = findAccount(facebookPages, change.after.fbPageId);
        const pageConnection = page ? findConnection(page.connectionId) : null;
        if (!page) {
          found.push({ parkiId: change.park.parkiId, message: `${parkName}: its Facebook page is no longer in your connected Facebook accounts. Press Refresh, then pick a page or choose None.` });
        } else if (pageConnection && pageConnection.status !== 'connected') {
          found.push({ parkiId: change.park.parkiId, message: `${parkName}: the Facebook sign-in that supplies ${page.name} has a problem. Refresh it or connect again.` });
        }
      }

      // A picked Instagram account must be listed and its sign-in must be working
      if (change.after.igAccountId) {
        const account = findAccount(instagramAccounts, change.after.igAccountId);
        const accountConnection = account ? findConnection(account.connectionId) : null;
        if (!account) {
          found.push({ parkiId: change.park.parkiId, message: `${parkName}: its Instagram account is no longer in your connected Facebook accounts. Press Refresh, then pick an account or choose None.` });
        } else if (accountConnection && accountConnection.status !== 'connected') {
          found.push({ parkiId: change.park.parkiId, message: `${parkName}: the Facebook sign-in that supplies ${instagramLabel(account)} has a problem. Refresh it or connect again.` });
        }
      }
    }
    return found;
  };

  // ---------------------------------------------------------------- #
  // Step 1: connect and disconnect
  // ---------------------------------------------------------------- #

  /**
   * Send the browser to Facebook's sign-in.
   */
  const handleConnect = async () => {
    // Leaving the app loses unsaved choices, so ask first
    if (isDirty()) {
      const userAgreed = window.confirm('Discard changes?');
      if (!userAgreed) {
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      // Ask the API for the sign-in address and go there
      const result = await startOAuth('meta', parkiId);
      window.location.href = result.data.authoriseUrl;
    } catch (err) {
      setError(err.message || 'Facebook sign-in could not be started.');
      setBusy(false);
    }
  };

  /**
   * Disconnect one Facebook sign-in, after the user confirms; history is kept.
   */
  const handleDisconnect = async (connection) => {
    // List the parks that use this sign-in
    const parkNames = [];
    for (const usedParkiId of connection.parkiIds || []) {
      parkNames.push(findParkName(register, usedParkiId));
    }
    let affectedText = 'No parks use it.';
    if (parkNames.length > 0) {
      affectedText = `These parks will stop receiving Facebook and Instagram data: ${joinNames(parkNames)}.`;
    }

    // Confirm before disconnecting
    const userAgreed = window.confirm(`Disconnect ${connection.externalUserName}'s Facebook sign-in? ${affectedText} Their history is kept.`);
    if (!userAgreed) {
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // Disconnect, then read the connections again
      const result = await disconnectMeta(connection.connectionId);
      const accountsResult = await getMetaAccounts();
      setAccounts(accountsResult.data);
      setChanged(true);

      // Tell the user whether Facebook itself was told; a login other Parki accounts use stays on at Facebook
      const otherAccountCount = result.data.sharedWithOtherEntities || 0;
      const loginIsShared = otherAccountCount > 0;
      if (loginIsShared) {
        setNotice(`Disconnected ${connection.externalUserName} from your parks. This Facebook login is also used by ${otherAccountCount} other Parki account(s), so access stays on at Facebook. Do not remove Parki in Facebook's settings.`);
      } else if (result.data.revokedAtMeta) {
        setNotice(`Disconnected ${connection.externalUserName}.`);
      } else {
        setNotice(`Disconnected ${connection.externalUserName} in Parki. Facebook could not be reached, so also remove Parki in Facebook's settings under Business integrations.`);
      }
    } catch (err) {
      setError(err.message || 'The Facebook sign-in could not be disconnected.');
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- #
  // Step 1: delete Meta data
  // ---------------------------------------------------------------- #

  /**
   * Tick or untick one park in the delete panel.
   */
  const handleDeleteParkTick = (parkKey, ticked) => {
    setDeleteParkKeys(previous => {
      return { ...previous, [parkKey]: ticked };
    });
  };

  /**
   * The parks ticked in the delete panel, in register order.
   */
  const listDeleteParks = () => {
    const tickedParks = [];
    for (const park of parks) {
      if (deleteParkKeys[String(park.parkiId)]) {
        tickedParks.push(park);
      }
    }
    return tickedParks;
  };

  /**
   * Delete all Facebook and Instagram history for the ticked parks.
   */
  const handleDeleteData = async () => {
    // Collect the ticked parks
    const tickedParks = listDeleteParks();
    const parkiIds = [];
    const parkNames = [];
    for (const park of tickedParks) {
      parkiIds.push(park.parkiId);
      parkNames.push(park.parkName);
    }

    // The typed word and at least one park are required
    const confirmed = deleteTyped === DELETE_CONFIRM_WORD;
    if (!confirmed || parkiIds.length === 0) {
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // Delete, sending the typed word for the API to check
      const result = await deleteMetaData(parkiIds, deleteTyped);

      // Add up the rows removed
      let rowsDeleted = 0;
      for (const entry of result.data.deleted || []) {
        rowsDeleted += Number(entry.rowsDeleted) || 0;
      }

      // Say what went, and close the panel
      setNotice(`Deleted ${rowsDeleted.toLocaleString()} rows of Facebook and Instagram history for ${joinNames(parkNames)}.`);
      setDeletePanelOpen(false);
      setDeleteParkKeys({});
      setDeleteTyped('');
    } catch (err) {
      setError(err.message || 'The Meta data could not be deleted.');
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- #
  // Step 2: list the accounts again
  // ---------------------------------------------------------------- #

  /**
   * Ask the API to list one connection's pages and Instagram accounts again.
   */
  const handleRefresh = async (connectionId) => {
    setRefreshingId(connectionId);
    setError(null);
    try {
      const result = await refreshMetaAccounts(connectionId);
      setAccounts(result.data);
    } catch (err) {
      setError(err.message || 'The Facebook pages could not be listed.');

      // The connection's status may have changed to error, so read it again
      try {
        const accountsResult = await getMetaAccounts();
        setAccounts(accountsResult.data);
      } catch (readError) {
        // Keep the first error on screen; this one adds nothing
      }
    } finally {
      setRefreshingId(null);
    }
  };

  /**
   * List every working connection again, one after another. Connections carried over from WordPress
   * have no Facebook user token, so they cannot be listed again and are skipped.
   */
  const refreshAllConnections = async () => {
    for (const connection of connections) {
      if (connection.status !== 'connected') {
        continue;
      }
      if (connection.canRefresh === false) {
        continue;
      }
      await handleRefresh(connection.connectionId);
    }
  };

  // List the accounts once when step 2 is first reached (not after coming back from Facebook)
  useEffect(() => {
    const shouldAutoRefresh = step === STEP_ACCOUNTS && !loading && connections.length > 0 && !autoRefreshDone.current;
    if (!shouldAutoRefresh) {
      return;
    }
    autoRefreshDone.current = true;
    refreshAllConnections();
  });

  // ---------------------------------------------------------------- #
  // Step 3: choices
  // ---------------------------------------------------------------- #

  /**
   * Change one field of one park's choices.
   */
  const updateChoice = (parkKey, field, value) => {
    // A new edit means the last save result no longer describes the screen
    setSaveSummary(null);
    setProblems([]);

    // Replace just this park's field
    setChoices(previous => {
      const parkChoices = { ...previous[parkKey], [field]: value };
      return { ...previous, [parkKey]: parkChoices };
    });
  };

  // ---------------------------------------------------------------- #
  // Step 4: save
  // ---------------------------------------------------------------- #

  /**
   * Save the page and Instagram choices, then the not-needed ticks.
   */
  const handleSave = async () => {
    const changes = buildChanges();

    // Build the save-mapping body: both fields for every park whose mapping changed
    const parksToSave = {};
    for (const change of changes) {
      if (!change.mappingChanged) {
        continue;
      }
      const parkKey = String(change.park.parkiId);
      parksToSave[parkKey] = {
        fbPageId: change.after.fbPageId || null,
        igAccountId: change.after.igAccountId || null
      };
    }
    const hasMappingChanges = Object.keys(parksToSave).length > 0;

    setBusy(true);
    setError(null);
    setProblems([]);
    try {
      // Save the page and Instagram choices
      let statusChanges = [];
      if (hasMappingChanges) {
        const result = await saveMetaMapping(parksToSave);
        statusChanges = result.data.statusChanges || [];
        setChanged(true);
      }

      // Save each not-needed tick that changed, one call per park and product
      let exclusionsSaved = 0;
      for (const change of changes) {
        if (change.facebookTickChanged) {
          await setExclusion(change.park.parkiId, 'facebook', change.after.facebookNotNeeded);
          exclusionsSaved += 1;
          setChanged(true);
        }
        if (change.instagramTickChanged) {
          await setExclusion(change.park.parkiId, 'instagram', change.after.instagramNotNeeded);
          exclusionsSaved += 1;
          setChanged(true);
        }
      }

      // Count the parks the register would not let us change
      let skippedCount = 0;
      for (const statusChange of statusChanges) {
        if (statusChange.skippedBecause) {
          skippedCount += 1;
        }
      }

      // The current choices are now the saved ones
      setSavedChoices(choices);
      setSaveSummary({
        parksSaved: Object.keys(parksToSave).length,
        exclusionsSaved: exclusionsSaved,
        skippedCount: skippedCount
      });
    } catch (err) {
      setError(err.message || 'The changes could not be saved.');
      const returnedProblems = err.data?.problems;
      if (Array.isArray(returnedProblems)) {
        setProblems(returnedProblems);
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Close the wizard, telling the page whether anything was saved.
   */
  const handleClose = () => {
    onClose(changed);
  };

  // ---------------------------------------------------------------- #
  // Render: shared pieces
  // ---------------------------------------------------------------- #

  /**
   * A centred spinner with optional text.
   */
  const renderSpinner = (text = null) => {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        {text && <div>{text}</div>}
      </div>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 1
  // ---------------------------------------------------------------- #

  /**
   * Draw the delete Meta data panel: park ticks, what will go, and the typed confirmation.
   */
  const renderDeletePanel = () => {
    // Say exactly what will be deleted
    const tickedParks = listDeleteParks();
    const tickedNames = [];
    for (const park of tickedParks) {
      tickedNames.push(park.parkName);
    }
    const confirmed = deleteTyped === DELETE_CONFIRM_WORD;
    const canDelete = confirmed && tickedParks.length > 0 && !busy;

    return (
      <div className="border border-danger rounded p-3 mt-3">
        <p className="mb-2">Tick the parks whose Facebook and Instagram history should be deleted from Parki. Their connection and settings are not changed.</p>

        {/* One tick per park */}
        <div className="mb-3" style={DELETE_PARK_LIST_STYLE}>
          {parks.map(park => {
            const parkKey = String(park.parkiId);
            const tickId = `meta-delete-${parkKey}`;
            return (
              <div key={parkKey} className="form-check mb-1">
                <input
                  id={tickId}
                  type="checkbox"
                  className="form-check-input"
                  checked={deleteParkKeys[parkKey] === true}
                  onChange={(event) => handleDeleteParkTick(parkKey, event.target.checked)}
                />
                <label htmlFor={tickId} className="form-check-label">{park.parkName}</label>
              </div>
            );
          })}
        </div>

        {/* What will go */}
        {tickedNames.length > 0 && (
          <div className="alert alert-danger" role="alert">
            All Facebook and Instagram history for {joinNames(tickedNames)}. This cannot be undone.
          </div>
        )}

        {/* The typed confirmation and the button */}
        <label htmlFor="meta-delete-confirm" className="form-label">Type {DELETE_CONFIRM_WORD} to confirm</label>
        <div className="d-flex flex-wrap gap-2">
          <input
            id="meta-delete-confirm"
            type="text"
            className="form-control"
            style={{ maxWidth: '12rem' }}
            autoComplete="off"
            value={deleteTyped}
            onChange={(event) => setDeleteTyped(event.target.value)}
          />
          <button type="button" className="btn btn-danger" onClick={handleDeleteData} disabled={!canDelete}>
            Delete data
          </button>
          <button type="button" className="btn btn-label-secondary" onClick={() => setDeletePanelOpen(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  /**
   * Draw the connected Facebook users and the Connect button, and the delete panel when it is open.
   */
  const renderConnectStep = () => {
    if (loading) {
      return renderSpinner();
    }

    // Staff viewing as the customer see a locked button; connecting is the customer's job
    let connectLabel = 'Connect Facebook & Instagram';
    if (connections.length > 0) {
      connectLabel = 'Connect another Facebook user';
    }
    let connectButton = (
      <button type="button" className="btn btn-primary" onClick={handleConnect} disabled={busy}>
        <i className="icon-base ti tabler-brand-facebook me-1"></i>
        {connectLabel}
      </button>
    );
    if (!canConnect) {
      connectButton = (
        <button type="button" className="btn btn-label-secondary" disabled title={IMPERSONATING_MESSAGE}>
          <i className="icon-base ti tabler-lock me-1"></i>
          Customer connects this
        </button>
      );
    }

    return (
      <>
        {notice && (
          <div className="alert alert-success" role="status">{notice}</div>
        )}

        {/* Delete Meta data, opened from the footer link */}
        {deletePanelOpen && (
          <div className="mb-4">
            {renderDeletePanel()}
          </div>
        )}

        {/* Not connected: one heading, one line, one button */}
        {connections.length === 0 && (
          <div className="integrations-connect-hero">
            <h4>Connect Facebook &amp; Instagram</h4>
            <p>One sign-in covers both.</p>
            {connectButton}
          </div>
        )}

        {/* Connected: one compact card per Facebook user, then the button to add another */}
        {connections.length > 0 && (
          <>
            {connections.map(connection => (
              <div key={connection.connectionId} className="integrations-connection-card mb-3">
                <ConnectionAvatar name={connection.externalUserName} />
                <div className="integrations-connection-card-text">
                  <div className="fw-medium">{connectionDisplayName(connection)}</div>
                  <div className="text-body-secondary small">
                    {describeConnectionCounts(connection)}
                  </div>
                  {connection.status === 'error' && (
                    <div className="text-danger small mt-1">{connection.lastError || 'Facebook refused the saved sign-in. Please reconnect.'}</div>
                  )}
                </div>
                <button type="button" className="btn btn-sm btn-label-danger" onClick={() => handleDisconnect(connection)} disabled={busy}>
                  Disconnect
                </button>
              </div>
            ))}
            <div className="text-center">
              {connectButton}
            </div>
          </>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 2
  // ---------------------------------------------------------------- #

  /**
   * Draw the counts and a Refresh button per connection.
   */
  const renderAccountsStep = () => {
    if (loading) {
      return renderSpinner();
    }
    if (connections.length === 0) {
      return <p className="mb-0">Connect Facebook &amp; Instagram first.</p>;
    }
    if (refreshing) {
      return renderSpinner('Asking Facebook for your pages and Instagram accounts...');
    }

    // Words for the counts
    const pageCount = facebookPages.length;
    const instagramCount = instagramAccounts.length;
    let pageWord = 'pages';
    if (pageCount === 1) {
      pageWord = 'page';
    }
    let instagramWord = 'accounts';
    if (instagramCount === 1) {
      instagramWord = 'account';
    }

    return (
      <>
        <p className="fs-5">Found {pageCount} Facebook {pageWord} and {instagramCount} Instagram {instagramWord}.</p>

        {/* One row per connection, with its own Refresh */}
        {connections.map(connection => (
          <div key={connection.connectionId} className="integrations-connection-card mb-3">
            <ConnectionAvatar name={connection.externalUserName} />
            <div className="integrations-connection-card-text">
              <div className="fw-medium">{connectionDisplayName(connection)}</div>
              {connection.lastFetchedAt && (
                <div className="text-body-secondary small">Last listed {formatDate(connection.lastFetchedAt)}.</div>
              )}
              {connection.status === 'error' && (
                <div className="text-danger small mt-1">{connection.lastError || 'Facebook refused the saved sign-in. Please reconnect.'}</div>
              )}
              {connection.canRefresh === false && (
                <div className="text-body-secondary small">Set up before the Integrations Register. Connect again to refresh this list.</div>
              )}
            </div>
            {connection.canRefresh !== false && (
              <button type="button" className="btn btn-sm btn-label-primary" onClick={() => handleRefresh(connection.connectionId)} disabled={refreshing || busy}>
                <i className="icon-base ti tabler-refresh me-1"></i>
                Refresh
              </button>
            )}
          </div>
        ))}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 3, the park list and the park's choices
  // ---------------------------------------------------------------- #

  /**
   * The fields, lists and labels for one product, so Facebook and Instagram share the drawing code below.
   */
  const productSettings = (productKey) => {
    // Facebook pages by default
    let settings = {
      items: facebookPages,
      filterText: pageFilter,
      setFilterText: setPageFilter,
      idField: 'fbPageId',
      tickField: 'facebookNotNeeded',
      title: 'Facebook page',
      noneLabel: 'No Facebook page',
      itemWord: 'pages',
      iconClass: 'tabler-brand-facebook',
      labelFor: pageLabel
    };

    // Instagram accounts
    if (productKey === 'instagram') {
      settings = {
        items: instagramAccounts,
        filterText: instagramFilter,
        setFilterText: setInstagramFilter,
        idField: 'igAccountId',
        tickField: 'instagramNotNeeded',
        title: 'Instagram account',
        noneLabel: 'No Instagram account',
        itemWord: 'accounts',
        iconClass: 'tabler-brand-instagram',
        labelFor: instagramLabel
      };
    }

    return settings;
  };

  /**
   * Work out where a park stands for one product: locked, not needed, chosen, missing or not chosen.
   */
  const describeProductState = (park, productKey) => {
    const parkKey = String(park.parkiId);
    const cell = park.cells?.[productKey] || {};
    const parkChoices = choices[parkKey] || {};
    const settings = productSettings(productKey);
    const chosenId = parkChoices[settings.idField] || '';
    const chosenAccount = findAccount(settings.items, chosenId);

    // The register will not let this cell change
    if (!cell.editable) {
      return { kind: 'locked', text: '' };
    }

    // Marked not needed
    if (parkChoices[settings.tickField] === true) {
      return { kind: 'not-needed', text: `${settings.title} not needed` };
    }

    // Nothing chosen
    if (chosenId === '') {
      return { kind: 'none', text: `no ${settings.title.toLowerCase()}` };
    }

    // Chosen, but Facebook no longer lists it
    if (!chosenAccount) {
      return { kind: 'missing', text: `${chosenId} (no longer available)` };
    }

    // Chosen and listed
    return { kind: 'chosen', text: settings.labelFor(chosenAccount) };
  };

  /**
   * The names of the other parks that use the same account as this one. A park marked not needed for
   * this product does not count, because it sends no data for it.
   */
  const listOtherParksUsing = (parkKey, idField, tickField, externalId) => {
    const parkNames = [];
    for (const park of parks) {
      const otherKey = String(park.parkiId);
      const isThisPark = otherKey === parkKey;
      if (isThisPark) {
        continue;
      }
      const otherChoices = choices[otherKey] || {};
      const otherIsNotNeeded = otherChoices[tickField] === true;
      if (otherIsNotNeeded) {
        continue;
      }
      const usesSameId = String(otherChoices[idField] || '') === String(externalId);
      if (usesSameId) {
        parkNames.push(park.parkName);
      }
    }
    return parkNames;
  };

  /**
   * Draw one park in the left-hand list: an icon for its state, its name and a one-line summary.
   */
  const renderParkListItem = (park, isActive) => {
    const facebookState = describeProductState(park, 'facebook');
    const instagramState = describeProductState(park, 'instagram');
    const isOpenedPark = parkiId !== null && String(park.parkiId) === String(parkiId);

    // The summary: what each product is set to, leaving out locked ones
    const summaryParts = [];
    if (facebookState.kind !== 'locked') {
      summaryParts.push(facebookState.text);
    }
    if (instagramState.kind !== 'locked') {
      summaryParts.push(instagramState.text);
    }
    const summary = summaryParts.join(' · ');

    // The icon: a warning when a choice is gone, a tick when both are settled, half when one is, dashed otherwise
    const states = [facebookState.kind, instagramState.kind];
    let settledCount = 0;
    let hasMissing = false;
    for (const kind of states) {
      const isSettled = kind === 'chosen' || kind === 'not-needed' || kind === 'locked';
      if (isSettled) {
        settledCount += 1;
      }
      if (kind === 'missing') {
        hasMissing = true;
      }
    }
    let iconClass = 'tabler-circle-dashed text-body-secondary';
    if (settledCount === 1) {
      iconClass = 'tabler-circle-half-2 text-warning';
    }
    if (settledCount === 2) {
      iconClass = 'tabler-circle-check text-success';
    }
    if (hasMissing) {
      iconClass = 'tabler-alert-triangle text-danger';
    }

    // The selected park is highlighted, and the opened park is scrolled into view
    let itemClass = 'integrations-mapper-item';
    if (isActive) {
      itemClass += ' is-active';
    }
    let itemRef = null;
    if (isOpenedPark) {
      itemRef = openedParkRow;
    }

    return (
      <button
        key={park.parkiId}
        ref={itemRef}
        type="button"
        className={itemClass}
        aria-current={isActive}
        onClick={() => setActiveParkKey(String(park.parkiId))}
      >
        <i className={`icon-base ti ${iconClass} integrations-mapper-item-icon`} aria-hidden="true"></i>
        <span className="integrations-mapper-item-text">
          <span className="integrations-mapper-item-name">{park.parkName}</span>
          {summary !== '' && (
            <span className="integrations-mapper-item-summary">{summary}</span>
          )}
        </span>
      </button>
    );
  };

  /**
   * Draw one product's section of the panel: its heading and "Not needed" switch, then a short searchable list to pick from.
   */
  const renderChoiceSection = (park, productKey) => {
    const parkKey = String(park.parkiId);
    const cell = park.cells?.[productKey] || {};
    const parkChoices = choices[parkKey] || {};
    const settings = productSettings(productKey);
    const chosenId = String(parkChoices[settings.idField] || '');
    const isNotNeeded = parkChoices[settings.tickField] === true;
    const switchId = `meta-${productKey}-not-needed-${parkKey}`;
    const radioName = `meta-${productKey}-${parkKey}`;

    // The register will not let this cell change: say why
    if (!cell.editable) {
      let lockedLabel = 'Not in plan';
      if (cell.state === 'contact-support') {
        lockedLabel = 'Contact support';
      }
      return (
        <div className="integrations-choice-section">
          <div className="integrations-mapper-panel-head">
            <span className="integrations-mapper-label">
              <i className={`icon-base ti ${settings.iconClass} me-1`} aria-hidden="true"></i>
              {settings.title}
            </span>
          </div>
          <p className="text-body-secondary small mb-0">{lockedLabel}. {cell.reason || ''}</p>
        </div>
      );
    }

    // Keep the accounts matching the filter, plus the current choice
    const filter = settings.filterText.trim().toLowerCase();
    const visibleItems = [];
    let chosenIsListed = false;
    for (const item of settings.items) {
      const isChosen = String(item.externalId) === chosenId;
      if (isChosen) {
        chosenIsListed = true;
      }
      const searchable = settings.labelFor(item).toLowerCase();
      const matchesFilter = filter === '' || searchable.includes(filter);
      if (!matchesFilter && !isChosen) {
        continue;
      }
      visibleItems.push(item);
    }

    // Group them by the Facebook user that supplies them, when there is more than one
    const groups = [];
    for (const connection of connections) {
      const groupItems = [];
      for (const item of visibleItems) {
        if (String(item.connectionId) === String(connection.connectionId)) {
          groupItems.push(item);
        }
      }
      if (groupItems.length > 0) {
        groups.push({ label: connection.externalUserName, connectionId: connection.connectionId, items: groupItems });
      }
    }
    if (groups.length === 0 && visibleItems.length > 0) {
      groups.push({ label: '', connectionId: 'all', items: visibleItems });
    }
    const showGroupLabels = groups.length > 1;

    // A mapped id that Facebook no longer lists still needs a row to show
    const showMissingRow = chosenId !== '' && !chosenIsListed;

    /**
     * Draw one choice row: a radio, the account's name and a note on which other park uses it. A row another park holds is disabled.
     */
    const renderChoiceRow = (value, label, usage, pictureUrl) => {
      const isOn = chosenId === value;
      let rowClass = 'integrations-choice-row';
      if (isOn) {
        rowClass += ' is-on';
      }
      if (usage.isTaken) {
        rowClass += ' is-taken';
      }
      let noteClass = 'integrations-choice-note';
      if (usage.isWarning) {
        noteClass += ' is-warning';
      }
      return (
        <label key={value || 'none'} className={rowClass}>
          <input
            type="radio"
            className="form-check-input"
            name={radioName}
            checked={isOn}
            disabled={usage.isTaken}
            onChange={() => updateChoice(parkKey, settings.idField, value)}
          />
          {pictureUrl && (
            <img src={pictureUrl} alt="" className="rounded-circle" style={PROFILE_PICTURE_STYLE} />
          )}
          <span className="integrations-choice-name">{label}</span>
          {usage.note !== '' && (
            <span className={noteClass}>{usage.note}</span>
          )}
        </label>
      );
    };

    /**
     * How an account is used elsewhere. Each account belongs to one park only, so a report never counts the
     * same page, profile or property twice: one another park holds is locked here, and if this park already
     * shares it (set up before this rule), the note warns so it can be fixed.
     */
    const describeUsage = (externalId) => {
      const otherParkNames = listOtherParksUsing(parkKey, settings.idField, settings.tickField, externalId);
      const otherCount = otherParkNames.length;
      const isChosenHere = chosenId === String(externalId);

      // Nobody else uses it: free to pick
      if (otherCount === 0) {
        return { note: '', isTaken: false, isWarning: false };
      }

      // Name the first other park, and count the rest
      let usedByText = otherParkNames[0];
      if (otherCount > 1) {
        usedByText = `${otherParkNames[0]} and ${otherCount - 1} more`;
      }

      // This park shares it with others already: keep it pickable so it can be let go, and warn
      if (isChosenHere) {
        return { note: `Also used by ${usedByText}`, isTaken: false, isWarning: true };
      }

      // Another park holds it: locked here until that park lets it go
      return { note: `Used by ${usedByText}`, isTaken: true, isWarning: false };
    };

    /** The usage of a row that is not an account (None, or an account no longer listed). */
    const noUsage = { note: '', isTaken: false, isWarning: false };

    return (
      <div className="integrations-choice-section">
        {/* Heading with the not-needed switch */}
        <div className="integrations-mapper-panel-head">
          <span className="integrations-mapper-label">
            <i className={`icon-base ti ${settings.iconClass} me-1`} aria-hidden="true"></i>
            {settings.title}
          </span>
          <div className="form-check form-switch mb-0">
            <input
              id={switchId}
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={isNotNeeded}
              onChange={(event) => updateChoice(parkKey, settings.tickField, event.target.checked)}
            />
            <label className="form-check-label" htmlFor={switchId}>Not needed</label>
          </div>
        </div>

        {/* Not needed: one line instead of the list */}
        {isNotNeeded && (
          <p className="text-body-secondary small mb-0">This park does not need a {settings.title.toLowerCase()}.</p>
        )}

        {/* The list to pick from, with its own filter */}
        {!isNotNeeded && (
          <div className="integrations-choice-list">
            <div className="integrations-choice-filter">
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder={`Filter ${settings.items.length} ${settings.itemWord}`}
                aria-label={`Filter ${settings.title.toLowerCase()}s`}
                value={settings.filterText}
                onChange={(event) => settings.setFilterText(event.target.value)}
              />
            </div>
            <div className="integrations-choice-rows" role="radiogroup" aria-label={settings.title}>
              {showMissingRow && renderChoiceRow(chosenId, `${chosenId} (no longer available)`, { note: 'Pick another', isTaken: false, isWarning: false }, null)}
              {groups.map(group => (
                <React.Fragment key={group.connectionId}>
                  {showGroupLabels && (
                    <div className="integrations-choice-group">{group.label}</div>
                  )}
                  {group.items.map(item => {
                    let pictureUrl = null;
                    if (productKey === 'instagram') {
                      pictureUrl = item.profilePictureUrl || null;
                    }
                    return renderChoiceRow(String(item.externalId), settings.labelFor(item), describeUsage(item.externalId), pictureUrl);
                  })}
                </React.Fragment>
              ))}
              {visibleItems.length === 0 && (
                <div className="integrations-choice-empty">Nothing matches "{settings.filterText}".</div>
              )}
              {renderChoiceRow('', settings.noneLabel, noUsage, null)}
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * Draw the Map parks step: the park list on the left and the selected park's Facebook and Instagram choices on the right.
   */
  const renderMapStep = () => {
    // Keep the parks whose name matches the search, split into mapped and not mapped
    const parkFilterText = parkFilter.trim().toLowerCase();
    const mappedParks = [];
    const unmappedParks = [];
    for (const park of parks) {
      const parkName = (park.parkName || '').toLowerCase();
      const matches = parkFilterText === '' || parkName.includes(parkFilterText);
      if (!matches) {
        continue;
      }
      const parkChoices = choices[String(park.parkiId)] || {};
      const hasAnyChoice = Boolean(parkChoices.fbPageId) || Boolean(parkChoices.igAccountId);
      const bothNotNeeded = parkChoices.facebookNotNeeded === true && parkChoices.instagramNotNeeded === true;
      if (hasAnyChoice || bothNotNeeded) {
        mappedParks.push(park);
      } else {
        unmappedParks.push(park);
      }
    }

    // The selected park: the one clicked, else the opened park, else the first park
    let activePark = null;
    for (const park of parks) {
      if (String(park.parkiId) === activeParkKey) {
        activePark = park;
      }
    }
    if (!activePark) {
      for (const park of parks) {
        const isOpenedPark = parkiId !== null && String(park.parkiId) === String(parkiId);
        if (isOpenedPark) {
          activePark = park;
        }
      }
    }
    if (!activePark && parks.length > 0) {
      activePark = parks[0];
    }
    let activeKey = null;
    if (activePark) {
      activeKey = String(activePark.parkiId);
    }

    return (
      <div className="integrations-mapper">
        {/* Left: search, then the mapped parks, then the rest */}
        <nav className="integrations-mapper-list" aria-label="Parks">
          <div className="integrations-mapper-search">
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Search parks"
              aria-label="Search parks"
              value={parkFilter}
              onChange={(event) => setParkFilter(event.target.value)}
            />
          </div>
          <div className="integrations-mapper-items">
            <div className="integrations-mapper-group">Mapped ({mappedParks.length})</div>
            {mappedParks.map(park => renderParkListItem(park, String(park.parkiId) === activeKey))}
            <div className="integrations-mapper-group">Not mapped ({unmappedParks.length})</div>
            {unmappedParks.map(park => renderParkListItem(park, String(park.parkiId) === activeKey))}
          </div>
        </nav>

        {/* Right: the selected park's choices */}
        {activePark && (
          <div className="integrations-mapper-panel">
            <h5 className="mb-0">{activePark.parkName}</h5>
            {renderChoiceSection(activePark, 'facebook')}
            {renderChoiceSection(activePark, 'instagram')}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 4
  // ---------------------------------------------------------------- #

  /**
   * Draw the list of changes, any problems, and the result once saved.
   */
  const renderReviewStep = () => {
    // After saving, say what happened
    if (saveSummary) {
      return (
        <>
          <div className="alert alert-success" role="status">Saved.</div>
          {saveSummary.skippedCount > 0 && (
            <p className="text-body-secondary">{saveSummary.skippedCount} park(s) could not be changed because they are not in your plan or need Parki support.</p>
          )}
        </>
      );
    }

    // List the changes park by park
    const changes = buildChanges();
    if (changes.length === 0) {
      return <p className="mb-0">No changes to save.</p>;
    }

    // Problems found here, else the ones the API sent back
    let shownProblems = checkChoices();
    if (shownProblems.length === 0) {
      shownProblems = problems;
    }

    return (
      <>
        <p>These changes will be saved:</p>
        <ul className="integrations-review-list">
          {changes.map(change => (
            <li key={change.park.parkiId}>
              <strong>{change.park.parkName}</strong>
              <ul>
                {change.pageChanged && (
                  <li>Facebook page: {describeChoice(facebookPages, change.before.fbPageId, pageLabel)} to {describeChoice(facebookPages, change.after.fbPageId, pageLabel)}</li>
                )}
                {change.instagramChanged && (
                  <li>Instagram account: {describeChoice(instagramAccounts, change.before.igAccountId, instagramLabel)} to {describeChoice(instagramAccounts, change.after.igAccountId, instagramLabel)}</li>
                )}
                {change.facebookTickChanged && (
                  <li>Facebook: {change.after.facebookNotNeeded ? 'marked not needed' : 'needed again'}</li>
                )}
                {change.instagramTickChanged && (
                  <li>Instagram: {change.after.instagramNotNeeded ? 'marked not needed' : 'needed again'}</li>
                )}
              </ul>
            </li>
          ))}
        </ul>

        {/* Problems, which block the save */}
        {shownProblems.length > 0 && (
          <div className="alert alert-danger" role="alert">
            <ul className="mb-0">
              {shownProblems.map((problem, index) => (
                <li key={index}>{problem.message}</li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Footer button for each step
  // ---------------------------------------------------------------- #

  // Next by default; Save on Review; Close once saved
  let primaryLabel = 'Next';
  let onPrimary = () => goToStep(step + 1);
  let primaryDisabled = false;
  if (step === STEP_CONNECT) {
    primaryDisabled = loading || connections.length === 0;
  } else if (step === STEP_ACCOUNTS) {
    primaryDisabled = refreshing || connections.length === 0;
  } else if (step === STEP_REVIEW) {
    if (saveSummary) {
      primaryLabel = 'Close';
      onPrimary = handleClose;
    } else {
      const noChanges = buildChanges().length === 0;
      const hasProblems = checkChoices().length > 0;
      primaryLabel = 'Save';
      onPrimary = handleSave;
      primaryDisabled = noChanges || hasProblems;
    }
  }

  /**
   * Keep step 3's ticks in step with a not-needed switch saved from the footer.
   */
  function handleNotNeededChange(productKey, notNeeded) {
    // Which tick the product maps to
    let choiceKey = 'instagramNotNeeded';
    if (productKey === 'facebook') {
      choiceKey = 'facebookNotNeeded';
    }

    // Saved and current choices both take the new value, so it is not counted as an unsaved change
    const parkKey = String(parkiId);
    setSavedChoices(previous => ({ ...previous, [parkKey]: { ...previous[parkKey], [choiceKey]: notNeeded } }));
    setChoices(previous => ({ ...previous, [parkKey]: { ...previous[parkKey], [choiceKey]: notNeeded } }));
    setChanged(true);
  }

  /**
   * Open the Delete Meta data panel on step 1.
   */
  function openDeletePanel() {
    setDeletePanelOpen(true);
    goToStep(STEP_CONNECT);
  }

  // The small "Delete data" link in the footer, shown when there are parks to delete for
  let deleteLink = null;
  const canOfferDelete = parks.length > 0 && !deletePanelOpen;
  if (canOfferDelete) {
    deleteLink = (
      <button type="button" className="btn btn-link btn-sm text-body-secondary p-0 ms-auto" onClick={openDeletePanel} disabled={busy}>
        Delete data
      </button>
    );
  }

  // Unsaved choices make closing ask first
  const dirty = isDirty();

  // ---------------------------------------------------------------- #
  // Render
  // ---------------------------------------------------------------- #

  return (
    <WizardFrame
      title="Facebook and Instagram"
      logoSrc={`${INTEGRATIONS_LOGO_PATH}/facebook.svg`}
      register={register}
      parkiId={parkiId}
      products={['facebook', 'instagram']}
      onNotNeededChange={handleNotNeededChange}
      footerStart={deleteLink}
      steps={META_STEPS}
      step={step}
      maxReachedStep={maxReachedStep}
      onStepChange={goToStep}
      error={error}
      dirty={dirty}
      onClose={handleClose}
      primaryLabel={primaryLabel}
      onPrimary={onPrimary}
      primaryDisabled={primaryDisabled}
      busy={busy}
      hideBack={Boolean(saveSummary)}
    >
      {step === STEP_CONNECT && renderConnectStep()}
      {step === STEP_ACCOUNTS && renderAccountsStep()}
      {step === STEP_MAP && renderMapStep()}
      {step === STEP_REVIEW && renderReviewStep()}
    </WizardFrame>
  );
};

export default MetaWizard;
